import { useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { Check, Sparkles, Store, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePaywallStore, type PaywallContext } from '@/stores/paywallStore';
import { usePlans, type Plan } from '@/hooks/queries/usePlans';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';

interface PaywallCopy {
  eyebrow: string;
  title: string;
  valueProp: React.ReactNode;
  valueSub: string;
  Icon: React.ComponentType<{ className?: string }>;
  isLimit: boolean;
}

function isCustomQuotePlan(
  plan: { name: string; price_monthly?: number } | null | undefined,
): boolean {
  if (!plan) return false;
  return (
    plan.name === 'business' ||
    plan.name === 'enterprise' ||
    (plan.price_monthly === 0 && plan.name !== 'free')
  );
}

function getOrderedPlans(plans: Plan[]): Plan[] {
  return [...plans]
    .filter((p) => p.is_active !== false)
    .sort((a, b) => {
      const aQuote = isCustomQuotePlan(a);
      const bQuote = isCustomQuotePlan(b);
      if (aQuote && !bQuote) return 1;
      if (!aQuote && bQuote) return -1;
      if (
        a.sort_order !== undefined &&
        b.sort_order !== undefined &&
        a.sort_order !== b.sort_order
      ) {
        return a.sort_order - b.sort_order;
      }
      return a.price_monthly - b.price_monthly;
    });
}

function buildCopy(
  ctx: PaywallContext,
  t: (key: string, opts?: Record<string, unknown>) => string,
  recommendedPlan?: Plan | null,
): PaywallCopy {
  if (ctx.reason === 'limit_reached') {
    const resource = ctx.resource ?? 'resources';
    const current = ctx.current ?? 0;
    const limit = ctx.limit ?? 0;

    let emContent: React.ReactNode = t(`paywall_limit_value_prop_em_${resource}`, {
      defaultValue: t('paywall_limit_value_prop_em_default'),
    });

    if (
      resource === 'shops' &&
      (isCustomQuotePlan(recommendedPlan) || recommendedPlan?.max_shops === null)
    ) {
      emContent = t('paywall_unlimited_shops', { defaultValue: 'des boutiques illimitées' });
    } else if (resource === 'shops' && recommendedPlan?.max_shops) {
      emContent = `jusqu'à ${recommendedPlan.max_shops} boutiques`;
    }

    return {
      eyebrow: t('paywall_limit_eyebrow'),
      title: t('paywall_limit_title', {
        current,
        limit,
        resource: t(`resource_${resource}`, { defaultValue: resource }),
      }),
      valueProp: (
        <>
          {t('paywall_limit_value_prop_pre')}{' '}
          <em className="not-italic font-bold text-lime-700">{emContent}</em>{' '}
          {t('paywall_limit_value_prop_post')}
        </>
      ),
      valueSub: t(`paywall_limit_value_sub_${resource}`, {
        defaultValue: t('paywall_limit_value_sub_default'),
      }),
      Icon: Store,
      isLimit: true,
    };
  }

  // feature_locked or generic
  const featureKey = ctx.feature ?? 'feature';
  return {
    eyebrow: t('paywall_feature_eyebrow'),
    title: t(`paywall_feature_title_${featureKey}`, {
      defaultValue: t('paywall_feature_title_default', {
        feature: t(`feature_${featureKey}`, { defaultValue: featureKey }),
      }),
    }),
    valueProp: (
      <>
        {t(`paywall_feature_value_prop_pre_${featureKey}`, {
          defaultValue: t('paywall_feature_value_prop_pre_default'),
        })}{' '}
        <em className="not-italic font-bold text-lime-700">
          {t(`paywall_feature_value_prop_em_${featureKey}`, {
            defaultValue: t('paywall_feature_value_prop_em_default'),
          })}
        </em>
      </>
    ),
    valueSub: t(`paywall_feature_value_sub_${featureKey}`, {
      defaultValue: t('paywall_feature_value_sub_default'),
    }),
    Icon: Sparkles,
    isLimit: false,
  };
}

export function FeaturePaywall() {
  const { t } = useTranslation('billing');
  const { open, context, closePaywall } = usePaywallStore();
  const { data: plansData } = usePlans();
  const { data: subData } = useSubscription();

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closePaywall();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closePaywall]);

  const currentPlan = subData?.subscription?.plan ?? null;
  const currentPlanName = currentPlan?.display_name ?? t('plan_free');
  const currentPlanPrice = currentPlan?.price_monthly ?? 0;
  const isCurrentFree = !currentPlan || currentPlan.price_monthly === 0;

  const recommendedPlan = useMemo(() => {
    if (!plansData || plansData.length === 0) return null;
    const ordered = getOrderedPlans(plansData);

    if (!currentPlan) {
      return (
        ordered.find((p) => p.name === 'croissance') ??
        ordered.find((p) => p.price_monthly > 0 && !isCustomQuotePlan(p)) ??
        ordered[0] ??
        null
      );
    }

    const currentIdx = ordered.findIndex(
      (p) => p.id === currentPlan.id || p.name === currentPlan.name,
    );

    if (currentIdx !== -1 && currentIdx + 1 < ordered.length) {
      return ordered[currentIdx + 1];
    }

    return ordered[ordered.length - 1] ?? null;
  }, [plansData, currentPlan]);

  if (!open || !context) return null;

  const copy = buildCopy(context, t, recommendedPlan);
  const isRecommendedQuote = isCustomQuotePlan(recommendedPlan);
  const heroBg = copy.isLimit
    ? 'bg-gradient-to-br from-warning-light to-lime-50'
    : 'bg-gradient-to-br from-lime-100 to-lime-50';
  const iconBg = copy.isLimit ? 'bg-warning' : 'bg-charcoal-950 text-lime-400';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal-950/70 backdrop-blur-sm p-4"
      onClick={closePaywall}
    >
      <div
        className="w-full max-w-[620px] overflow-hidden rounded-2xl border-[2.5px] border-charcoal-950 bg-white shadow-[6px_6px_0_#0d0d1a]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn('relative px-7 py-6 border-b-[2.5px] border-charcoal-950', heroBg)}>
          <div className="flex items-center gap-3.5">
            <div
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-md border-[2.5px] border-charcoal-950 shadow-[3px_3px_0_#0d0d1a]',
                iconBg,
              )}
            >
              <copy.Icon className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-charcoal-700 mb-1">
                {copy.eyebrow}
              </p>
              <h3
                id="paywall-title"
                className="font-heading text-xl sm:text-[22px] font-extrabold text-charcoal-950 leading-tight"
              >
                {copy.title}
              </h3>
            </div>
          </div>
          <button
            onClick={closePaywall}
            aria-label={t('paywall_close')}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md border-2 border-charcoal-950 bg-charcoal-950 text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="p-6 sm:p-7">
          <p className="font-heading text-[17px] font-semibold leading-snug text-charcoal-950">
            {copy.valueProp}
          </p>
          <p className="mt-1 mb-5 text-sm text-charcoal-600">{copy.valueSub}</p>

          {copy.isLimit && context.current !== undefined && context.limit !== undefined && (
            <div className="mb-5 rounded-md border-2 border-charcoal-950 bg-charcoal-50 px-4 py-3.5">
              <div className="mb-2 flex items-center justify-between">
                <b className="font-heading text-sm font-bold">
                  {t(`resource_${context.resource ?? 'items'}`, { defaultValue: context.resource })}
                </b>
                <span className="font-mono text-xs font-bold text-error">
                  {context.current} / {context.limit}
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded border-[1.5px] border-charcoal-950 bg-white">
                <span className="block h-full bg-error" style={{ width: '100%' }} />
              </div>
            </div>
          )}

          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="overflow-hidden rounded-md border-2 border-charcoal-950">
              <div className="flex items-center justify-between border-b-2 border-charcoal-950 bg-charcoal-50 px-3.5 py-2.5">
                <span className="font-heading text-[13px] font-bold uppercase tracking-[0.06em]">
                  {t('paywall_current_plan')}
                </span>
                <span className="font-mono text-[11px] font-bold uppercase tracking-wide">
                  {currentPlanName.toUpperCase()}
                </span>
              </div>
              <div className="bg-white px-3.5 py-3 text-sm text-charcoal-700">
                <div
                  className={cn(
                    'mb-2 font-heading text-xl font-extrabold',
                    isCurrentFree && 'text-charcoal-400',
                  )}
                >
                  {currentPlanPrice > 0 ? formatCurrency(currentPlanPrice, 'XOF') : t('free')}
                  <small className="ml-1 font-sans text-xs font-medium text-charcoal-500">
                    {t('per_month')}
                  </small>
                </div>
                <ul className="space-y-1">
                  <li className="flex items-start gap-2 text-charcoal-700">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-charcoal-400" />
                    <span>{t('paywall_current_basic')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-charcoal-400 line-through">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {copy.isLimit
                        ? t(`resource_${context.resource ?? 'items'}`, { defaultValue: 'Resource' })
                        : t(`feature_${context.feature ?? 'feature'}`, {
                            defaultValue: context.feature,
                          })}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-md border-2 border-charcoal-950">
              <div className="relative flex items-center justify-between border-b-2 border-charcoal-950 bg-lime-500 px-3.5 py-2.5 text-charcoal-950">
                <span className="font-heading text-[13px] font-bold uppercase tracking-[0.06em]">
                  {recommendedPlan?.display_name ?? t('paywall_pro_plan')}
                </span>
                <span className="font-mono text-[11px] font-bold uppercase tracking-wide">
                  {isRecommendedQuote
                    ? t('paywall_badge_quote', { defaultValue: 'Sur Devis' })
                    : t('paywall_recommended_badge')}
                </span>
              </div>
              <div className="bg-white px-3.5 py-3 text-sm">
                <div className="mb-2 font-heading text-xl font-extrabold">
                  {isRecommendedQuote ? (
                    <span className="text-lime-800">
                      {t('custom_quote', { defaultValue: 'Sur devis' })}
                    </span>
                  ) : recommendedPlan ? (
                    <>
                      {formatCurrency(recommendedPlan.price_monthly, 'XOF')}
                      <small className="ml-1 font-sans text-xs font-medium text-charcoal-500">
                        {t('per_month')}
                      </small>
                    </>
                  ) : (
                    '—'
                  )}
                </div>
                <ul className="space-y-1">
                  <li className="flex items-start gap-2 text-charcoal-700">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-600" />
                    <b className="font-bold text-charcoal-950">
                      {copy.isLimit
                        ? isRecommendedQuote ||
                          (context.resource === 'shops' && recommendedPlan?.max_shops === null)
                          ? t('paywall_unlock_resource', {
                              resource: t(`resource_${context.resource ?? 'items'}`, {
                                defaultValue: context.resource,
                              }),
                            })
                          : recommendedPlan?.max_shops
                            ? `Multi-boutiques (jusqu'à ${recommendedPlan.max_shops})`
                            : t('paywall_unlock_resource', {
                                resource: t(`resource_${context.resource ?? 'items'}`, {
                                  defaultValue: context.resource,
                                }),
                              })
                        : t(`feature_${context.feature ?? 'feature'}`, {
                            defaultValue: context.feature,
                          })}
                    </b>
                  </li>
                  <li className="flex items-start gap-2 text-charcoal-700">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-600" />
                    <span>
                      {isRecommendedQuote
                        ? t('paywall_quote_feature_1', {
                            defaultValue: 'Accompagnement & support dédié',
                          })
                        : recommendedPlan?.name === 'pro'
                          ? t('paywall_pro_extra_1')
                          : t('paywall_pro_extra_1', {
                              defaultValue: 'Fonctionnalités avancées incluses',
                            })}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-charcoal-700">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-600" />
                    <span>
                      {isRecommendedQuote
                        ? t('paywall_quote_feature_2', { defaultValue: 'Intégrations sur-mesure' })
                        : t('paywall_pro_extra_2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-3 flex flex-col gap-2.5 sm:flex-row">
            <Link
              to="/subscription/plans"
              className="flex-1 inline-flex items-center justify-center rounded-md border-[2.5px] border-charcoal-950 bg-lime-500 px-5 py-3.5 font-heading text-sm font-bold text-charcoal-950 shadow-[3px_3px_0_#0d0d1a] transition-transform hover:-translate-x-px hover:-translate-y-px hover:shadow-[5px_5px_0_#0d0d1a]"
              onClick={closePaywall}
            >
              {isRecommendedQuote
                ? t('paywall_cta_quote', { defaultValue: 'Demander un devis' })
                : t('paywall_cta_upgrade_plan', {
                    plan: recommendedPlan?.display_name ?? 'Pro',
                    defaultValue: `Passer au plan ${recommendedPlan?.display_name ?? 'Pro'}`,
                  })}
            </Link>
            <Link
              to="/subscription/plans"
              className="flex-1 inline-flex items-center justify-center rounded-md border-[2.5px] border-charcoal-950 bg-white px-5 py-3 font-heading text-sm font-bold text-charcoal-950 hover:bg-charcoal-50"
              onClick={closePaywall}
            >
              {t('paywall_cta_compare')}
            </Link>
          </div>

          <button
            onClick={closePaywall}
            className="block w-full px-2 py-2 text-center text-xs font-semibold text-charcoal-500 underline underline-offset-[3px] hover:text-charcoal-950"
          >
            {t('paywall_cta_not_now')}
          </button>

          <p className="mt-3 text-center text-xs text-charcoal-500">
            <Check className="mr-1 inline-block h-3 w-3 text-lime-600" />
            {t('paywall_footer_trust')}
          </p>
        </div>
      </div>
    </div>
  );
}
